import { useEffect, useRef, useState } from 'react'
import { MessageCircle, Send, Wifi, WifiOff } from 'lucide-react'
import consumer from '@/lib/cable'

interface Message {
    type: 'ping' | 'echo'
    message: string
    timestamp: string
    counter?: number
    from_client?: boolean
}

export default function ActionCableExample() {
    const [isConnected, setIsConnected] = useState(false)
    const [messages, setMessages] = useState<Message[]>([])
    const [inputMessage, setInputMessage] = useState('')
    const [isExpanded, setIsExpanded] = useState(false)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const subscription = consumer.subscriptions.create(
            {channel: "TestChannel"},
            {
                connected() {
                    console.log("Connected to TestChannel")
                    setIsConnected(true)
                },

                disconnected() {
                    console.log("Disconnected from TestChannel")
                    setIsConnected(false)
                },

                received(data: Message) {
                    console.log("Received data:", data)
                    setMessages(prev => [...prev.slice(-9), data]) // Keep last 10 messages
                }
            }
        )

        return () => {
            subscription.unsubscribe()
        }
    }, [])

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
    }, [messages])

    const sendMessage = () => {
        if (!inputMessage.trim() || !isConnected) return

        consumer.subscriptions.subscriptions.forEach((subscription: any) => {
            if (subscription.identifier.includes('TestChannel')) {
                subscription.send({message: inputMessage})
            }
        })

        setInputMessage('')
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            sendMessage()
        }
    }

    return (
        <div className="bg-white rounded-lg shadow-lg border">
            {/* Header */}
            <div
                className="flex items-center justify-between p-4 cursor-pointer border-b hover:bg-gray-50"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center space-x-3">
                    <MessageCircle className="h-5 w-5 text-blue-600"/>
                    <h3 className="text-lg font-semibold text-gray-900">Action Cable Test</h3>
                    <div className="flex items-center space-x-2">
                        {isConnected ? (
                            <>
                                <Wifi className="h-4 w-4 text-green-500"/>
                                <span className="text-sm text-green-600 font-medium">Connected</span>
                            </>
                        ) : (
                            <>
                                <WifiOff className="h-4 w-4 text-red-500"/>
                                <span className="text-sm text-red-600 font-medium">Disconnected</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {messages.length} messages
          </span>
                    <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        ▼
                    </div>
                </div>
            </div>

            {/* Expandable Content */}
            {isExpanded && (
                <div className="p-4">
                    {/* Message Log */}
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Live Messages</h4>
                        <div className="bg-gray-50 rounded-lg p-3 h-48 overflow-y-auto border">
                            {messages.length === 0 ? (
                                <p className="text-gray-500 text-sm italic">
                                    Waiting for messages... Server should ping every 5 seconds
                                </p>
                            ) : (
                                messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`mb-2 p-2 rounded text-sm ${
                                            msg.type === 'ping'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-green-100 text-green-800'
                                        }`}
                                    >
                                        <div className="flex justify-between items-start">
                      <span className="font-medium">
                        {msg.type === 'ping' ? '📡 Server' : '↩️ Echo'}: {msg.message}
                      </span>
                                            <span className="text-xs opacity-75">{msg.timestamp}</span>
                                        </div>
                                        {msg.counter && (
                                            <div className="text-xs mt-1">Counter: {msg.counter}</div>
                                        )}
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef}/>
                        </div>
                    </div>

                    {/* Send Message */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Send Test Message</h4>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type a message to test bidirectional communication..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                disabled={!isConnected}
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!isConnected || !inputMessage.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                <Send className="h-4 w-4"/>
                                <span>Send</span>
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            {isConnected
                                ? "Messages will be echoed back by the server"
                                : "Connect to start testing"}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}